import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  ORDER_LINE_TYPE_COPY,
  ORDER_LINE_TYPE_FILTER_OPTIONS,
  type OrderLineTypeFilterOption,
} from "../../lib/order-line-kind";

const OPTION_COPY: Record<
  OrderLineTypeFilterOption,
  { key: string; fallback: string }
> = {
  all: { key: "marketplace.orders.list.typeFilterAll", fallback: "All types" },
  ...ORDER_LINE_TYPE_COPY,
};

/** Single-select Type filter; each view supplies its own trigger button. */
export function OrderLineTypeFilterMenu({
  value,
  onChange,
  renderTrigger,
}: {
  value: OrderLineTypeFilterOption;
  onChange: (next: OrderLineTypeFilterOption) => void;
  renderTrigger: (trigger: { label: string; isActive: boolean }) => ReactNode;
}) {
  const { t } = useTranslation();
  const title = t("marketplace.orders.list.typeFilter", "Type");
  const isActive = value !== "all";
  const label = isActive
    ? t("marketplace.orders.list.typeFilterActive", {
        type: t(OPTION_COPY[value].key, OPTION_COPY[value].fallback),
        defaultValue: "Type: {{type}}",
      })
    : title;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {renderTrigger({ label, isActive })}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {title}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => onChange(next as OrderLineTypeFilterOption)}
        >
          {ORDER_LINE_TYPE_FILTER_OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option} value={option} className="text-xs">
              {t(OPTION_COPY[option].key, OPTION_COPY[option].fallback)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
